import type {
    FC,
    KeyboardEvent,
    PointerEvent,
    ReactNode,
    RefObject
} from 'react'

import {
    useEffect,
    useId,
    useRef,
    useState
} from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

/*
 * One overlay for the whole app: a bottom sheet on phones that can be
 * dragged down to close, and a centred dialog on larger screens. It traps
 * focus, closes on Escape, locks page scroll and gives focus back to the
 * button that opened it.
 */

type SheetSize = 'sm' | 'md' | 'lg' | 'xl'

interface SheetProps {
    open: boolean
    onClose: () => void
    title: ReactNode
    description?: ReactNode
    children?: ReactNode
    footer?: ReactNode
    size?: SheetSize
    initialFocus?: RefObject<HTMLElement | null>
    dismissible?: boolean
    bodyClassName?: string
}

const SIZES: Record<SheetSize, string> = {
    sm: 'sm:max-w-md',
    md: 'sm:max-w-lg',
    lg: 'sm:max-w-2xl',
    xl: 'sm:max-w-3xl'
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

let openCount = 0

const lockScroll = () => {
    openCount += 1
    if (openCount !== 1) return

    const scrollbar = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`
}

const unlockScroll = () => {
    openCount = Math.max(0, openCount - 1)
    if (openCount !== 0) return

    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
}

const TRANSITION_MS = 220

export const Sheet: FC<SheetProps> = ({
    open,
    onClose,
    title,
    description,
    children,
    footer,
    size = 'md',
    initialFocus,
    dismissible = true,
    bodyClassName
}): ReactNode => {
    const [mounted, setMounted] = useState(false)
    const [visible, setVisible] = useState(false)
    const [dragOffset, setDragOffset] = useState(0)
    const [dragging, setDragging] = useState(false)

    const panelRef = useRef<HTMLDivElement>(null)
    const returnFocusRef = useRef<HTMLElement | null>(null)
    const dragRef = useRef<{ startY: number, startTime: number } | null>(null)
    const onCloseRef = useRef(onClose)

    const titleId = useId()
    const descriptionId = useId()

    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    useEffect(() => {
        if (open) {
            setMounted(true)
            setDragOffset(0)

            const frame = requestAnimationFrame(() => {
                requestAnimationFrame(() => setVisible(true))
            })

            return () => cancelAnimationFrame(frame)
        }

        setVisible(false)

        const timer = window.setTimeout(() => setMounted(false), TRANSITION_MS)
        return () => window.clearTimeout(timer)
    }, [open])

    useEffect(() => {
        if (!mounted) return

        lockScroll()
        returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null

        const panel = panelRef.current

        return () => {
            unlockScroll()

            // Hand focus back only if it was still in this sheet, so a sheet that
            // opened in its place keeps focus.
            const active = document.activeElement
            const focusWasHere = !active || active === document.body || Boolean(panel && panel.contains(active))
            const target = returnFocusRef.current

            if (focusWasHere && target && document.contains(target)) target.focus({ preventScroll: true })
        }
    }, [mounted])

    useEffect(() => {
        if (!visible) return

        const panel = panelRef.current
        if (!panel) return

        const target = initialFocus?.current
        if (target) target.focus({ preventScroll: true })
        else panel.focus({ preventScroll: true })
    }, [visible, initialFocus])

    const close = () => {
        if (dismissible) onCloseRef.current()
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.stopPropagation()
            close()
            return
        }

        if (event.key !== 'Tab') return

        const panel = panelRef.current
        if (!panel) return

        const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(item => item.offsetParent !== null || item === document.activeElement)
        if (!items.length) {
            event.preventDefault()
            return
        }

        const first = items[0]
        const last = items[items.length - 1]

        if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
            event.preventDefault()
            last.focus()
        }
        else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first.focus()
        }
    }

    const onDragStart = (event: PointerEvent<HTMLDivElement>) => {
        if (!dismissible || window.matchMedia('(min-width: 640px)').matches) return

        dragRef.current = { startY: event.clientY, startTime: Date.now() }
        setDragging(true)
        event.currentTarget.setPointerCapture(event.pointerId)
    }

    const onDragMove = (event: PointerEvent<HTMLDivElement>) => {
        if (!dragRef.current) return
        setDragOffset(Math.max(0, event.clientY - dragRef.current.startY))
    }

    const onDragEnd = (event: PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current
        if (!drag) return

        const distance = Math.max(0, event.clientY - drag.startY)
        const speed = distance / Math.max(1, Date.now() - drag.startTime)

        dragRef.current = null
        setDragging(false)

        if (distance > 110 || (distance > 40 && speed > 0.6)) close()
        else setDragOffset(0)
    }

    if (!mounted) return null

    return createPortal(
        <div className='fixed inset-0 z-50' role='presentation'>
            <div
                aria-hidden='true'
                onClick={close}
                className={cn(
                    'absolute inset-0 bg-slate-950/45 backdrop-blur-[2px] transition-opacity duration-200',
                    visible ? 'opacity-100' : 'opacity-0'
                )}
            />

            <div
                ref={panelRef}
                role='dialog'
                aria-modal='true'
                aria-labelledby={titleId}
                aria-describedby={description ? descriptionId : undefined}
                tabIndex={-1}
                onKeyDown={onKeyDown}
                style={dragOffset ? { transform: `translateY(${dragOffset}px)` } : undefined}
                className={cn(
                    'fixed flex flex-col bg-card text-card-foreground shadow-float outline-none',
                    'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-[1.35rem]',
                    'sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[86vh] sm:w-[calc(100%-2rem)] sm:-translate-x-1/2 sm:rounded-2xl',
                    dragging ? 'transition-none' : 'transition-[translate,opacity,transform] duration-200 ease-out',
                    visible
                        ? 'translate-y-0 sm:-translate-y-1/2 sm:opacity-100'
                        : 'translate-y-full sm:translate-y-[calc(-50%_+_1rem)] sm:opacity-0',
                    SIZES[size]
                )}
            >
                <div
                    className='flex touch-none justify-center pt-2.5 pb-1 sm:hidden'
                    onPointerDown={onDragStart}
                    onPointerMove={onDragMove}
                    onPointerUp={onDragEnd}
                    onPointerCancel={onDragEnd}
                >
                    <span className='h-1.5 w-10 rounded-full bg-slate-300' aria-hidden='true' />
                </div>

                <div
                    className='flex touch-none items-start gap-3 px-5 pt-1.5 pb-3 sm:touch-auto sm:pt-5'
                    onPointerDown={onDragStart}
                    onPointerMove={onDragMove}
                    onPointerUp={onDragEnd}
                    onPointerCancel={onDragEnd}
                >
                    <div className='min-w-0 flex-1'>
                        <h2 id={titleId} className='font-heading text-lg leading-tight font-bold text-foreground'>
                            {title}
                        </h2>

                        {description && (
                            <p id={descriptionId} className='mt-1 text-sm leading-snug text-muted-foreground'>
                                {description}
                            </p>
                        )}
                    </div>

                    {dismissible && (
                        <button
                            type='button'
                            onClick={close}
                            onPointerDown={event => event.stopPropagation()}
                            aria-label='Close'
                            className='-mt-1 -mr-2 grid size-10 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-slate-100 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
                        >
                            <X className='size-5' aria-hidden='true' />
                        </button>
                    )}
                </div>

                <div className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-5', bodyClassName)}>
                    {children}
                </div>

                {footer
                    ? (
                        <div className='border-t border-border bg-card px-5 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:rounded-b-2xl sm:pb-4'>
                            {footer}
                        </div>
                    )
                    : <div className='pb-[env(safe-area-inset-bottom)]' />}
            </div>
        </div>,
        document.body
    )
}
